import PropTypes from 'prop-types';
import React from 'react';
import SingleAemEspot from '../../SingleAemEspot';
import MenuSelector from './MenuSelector';
import styles from './menu.module.scss';

/**
 * MenuList component loops through a subset of links in a category or footer section
 *
 * @param {Object} props - The props for the MenuList component.
 * @param {Array} props.links - An array of categories or links.
 * @param {string} props.links[].label - The name of the link.
 * @param {string} props.links[].id - The id of the link.
 * @param {string} [props.links[].link] - The URL, if the link should not open
 * another menu level.
 * @param {boolean} [props.links[].hasSpecialNavInteraction] - Whether the link title
 * is an espot.
 * @param {string} [props.links[].espotIdentifier] - Identifier for the espot in place of
 * the link title.
 * @param {Function} props.onClick - The click handler that will open the next menu level.
 * @param {string} props.theme - Primary (L0), secondary (L1+), or tertiary (footer).
 * @param {boolean} [props.isClearance] - Whether this is a clearance link (with
 * special styling).
 *
 * @returns {JSX.Element} The rendered MenuList component.
 */
const MenuList = ({
  links, handleClick, theme, isClearance = false, topEspotIdentifier, bottomEspotIdentifier,
}) => (
  <>
    {topEspotIdentifier ? <SingleAemEspot espotId={topEspotIdentifier} /> : null}
    {links.length > 0 ? (
      <ul className={styles.list}>
        {links.map(({
          id,
          label,
          link,
          hasSpecialNavInteraction = false,
          espotIdentifier = null,
          external = false,
        }) => (
          <li key={id}>
            <MenuSelector
              isClearance={isClearance}
              isExternal={external}
              link={link}
              onClick={!link ? () => handleClick(id) : undefined}
              theme={theme}
            >
              {hasSpecialNavInteraction === true && espotIdentifier
                ? <SingleAemEspot espotId={`${espotIdentifier}-nav-special-interaction-mobile`} />
                : label}
            </MenuSelector>
          </li>
        ))}
      </ul>
    ) : null}
    {bottomEspotIdentifier ? <SingleAemEspot espotId={bottomEspotIdentifier} /> : null}
  </>
);

MenuList.propTypes = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
      hasSpecialNavInteraction: PropTypes.bool,
      espotIdentifier: PropTypes.string,
      id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  handleClick: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  isClearance: PropTypes.bool,
  topEspotIdentifier: PropTypes.string,
  bottomEspotIdentifier: PropTypes.string,
};

export default MenuList;
